import { useQueries, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import zeusRqServiceApi, { type ChunkContent } from '../../api/zeus-rq.service.api';

/**
 * React-query hooks for knowledge-item chunks (zeus). All consumers should
 * reach the chunk API through these hooks.
 */

export const chunksKeys = {
  all: ['chunks'] as const,
  detail: (id: string) => [...chunksKeys.all, 'detail', id] as const,
};

export interface UseChunksResult {
  /** Chunks fetched so far; failed ids are omitted. */
  chunks: ChunkContent[];
  isLoading: boolean;
  /** Every requested chunk failed to load. */
  isError: boolean;
}

/** Merge per-chunk query results into one view-friendly shape. */
export function combineChunkResults(results: UseQueryResult<ChunkContent>[]): UseChunksResult {
  return {
    chunks: results.filter(result => result.isSuccess).map(result => result.data),
    isLoading: results.some(result => result.isLoading),
    isError: results.length > 0 && results.every(result => result.isError),
  };
}

// One chunk's cached query. zeus authorizes the read by id (own-org or a
// hub-shared chunk the caller is entitled to), so there's one endpoint and one
// key per id. Content is immutable (edits produce new ids), so it's cached for
// the session; the org-switch cache clear still applies. A 404 (hallucinated id)
// is terminal — don't retry it.
function chunkQuery(chunkId: string) {
  return {
    queryKey: chunksKeys.detail(chunkId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      zeusRqServiceApi.getChunkById(chunkId, { signal }),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount: number, error: Error) =>
      isAxiosError(error) && error.response?.status === 404 ? false : failureCount < 3,
  };
}

/**
 * Warm the chunk cache ahead of render into the same keys `useChunks` reads.
 * Used when a hub-answer run starts (from the offer's approved chunk ids) so the
 * citations are ready the moment the answer lands, in parallel with generation.
 */
export function prefetchChunks(queryClient: QueryClient, chunkIds: string[]): void {
  chunkIds.forEach(chunkId => {
    void queryClient.prefetchQuery(chunkQuery(chunkId));
  });
}

/** Content of the given chunks, one cached query per chunk id. */
export function useChunks(chunkIds: string[]): UseChunksResult {
  return useQueries({
    queries: chunkIds.map(chunkId => chunkQuery(chunkId)),
    combine: combineChunkResults,
  });
}
